import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/useAuthStore'
import { useOnboarding } from '@/hooks/useOnboarding'
import { OnboardingWizard } from './OnboardingWizard'

// Don't pop the wizard over the auth / verification flow itself.
const SUPPRESSED_PREFIXES = ['/signup', '/verify-email', '/auth/action', '/admin']

/**
 * App-level host for the first-login onboarding wizard. Auto-opens the guided
 * stepper once for a fresh account with outstanding steps, then never again once
 * the user skips or finishes (persisted on their profile). Subsumes the old
 * standalone GST reminder banner — GST is now one step in the checklist.
 */
export function OnboardingLauncher() {
  const user = useAuthStore((s) => s.user)
  const { steps, progress, shouldAutoShow, dismiss, complete } = useOnboarding()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  // Ensures we only auto-open once per session, even as the profile re-renders.
  const autoShownRef = useRef(false)

  const suppressed = SUPPRESSED_PREFIXES.some((p) => pathname.startsWith(p))
  const emailVerified = user?.emailVerified ?? false

  useEffect(() => {
    if (autoShownRef.current) return
    if (!emailVerified || suppressed) return
    if (shouldAutoShow) {
      autoShownRef.current = true
      setOpen(true)
    }
  }, [emailVerified, suppressed, shouldAutoShow])

  if (!open) return null

  return (
    <OnboardingWizard
      isOpen={open}
      steps={steps}
      progress={progress}
      onSkip={() => {
        setOpen(false)
        void dismiss()
      }}
      onFinish={() => {
        setOpen(false)
        void complete()
      }}
      onNavigate={() => setOpen(false)}
    />
  )
}
